import { useState, useEffect } from 'react'
import { Box, Card, Grid, Toolbar, IconButton, Modal, Fab } from '@mui/material'
import { ArrowBackIos, Close } from '@mui/icons-material'
import { CreateUser, PermissionUser } from '../../containers'
import { useNavigate, useParams } from 'react-router-dom'
import { usersRoutes } from '../../routes'
import { useUsersContext, useAuth, useStatus } from '../../context'
import { LoadingLogoDialog } from '../../dialogs'
import { GenerationStatusModal } from '../../dialogs/StatusModal'

const styleModal = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    borderRadius: 20,
    boxShadow: 24,
    p: 4
}

const userDataDefault = {
    rut: '',
    name: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    sites: '',
    obras: [],
    role: null,
    enabled: true,
    mailNotification: false
}

const AdminNewUserPage = () => {
    const { createUser, getUsers, users } = useUsersContext()
    const { userData: loggedInUserData } = useAuth()
    const { setStatus } = useStatus()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const [openLoader, setOpenLoader] = useState(false)
    const [loadingData, setLoadingData] = useState('')
    const [routingData, setRoutingData] = useState('')
    const [usageModule, setUsageModule] = useState(0)
    const [userData, setUserData] = useState(userDataDefault)

    const { id } = useParams()

    const openCloseModal = () => {
        let answer
        if (open) {
            answer = false
            navigate(-1)
            deleteData()
        } else {
            answer = true
        }
        setTimeout(() => {
            setOpen(answer)
        }, 500)
    }

    useEffect(() => {
        const initPage = async () => {
            if (id) {
                setRoutingData('Editar usuario')
                setUsageModule(0)

                const userInContext = (users || []).find(u => u._id === id)

                if (userInContext) {
                    setUserData({
                        ...userInContext,
                        role: userInContext.role || null,
                        roleName: userInContext.role?.name,
                        roleDbName: userInContext.role?.dbName
                    })
                } else {
                    try {
                        const response = await usersRoutes.getUser(id)
                        if (response.data) {
                            const fetchedUser = response.data
                            setUserData({
                                ...fetchedUser,
                                role: fetchedUser.role || null,
                                roleName: fetchedUser.role?.name,
                                roleDbName: fetchedUser.role?.dbName
                            })
                        }
                    } catch (error) {
                        console.error('Error fetching user data:', error)
                    }
                }
            } else {
                setUserData(userDataDefault)
                setUsageModule(0)
                setRoutingData('Nuevo usuario')
            }
        }

        initPage()

        return () => {
            setUserData(userDataDefault)
        }
    }, [id, users])

    const saveUser = async () => {
        setOpenLoader(true)
        setLoadingData('Inscribiendo nuevo usuario')

        const payload = {
            name: userData.name,
            lastName: userData.lastName,
            rut: userData.rut,
            email: userData.email,
            phone: userData.phone,
            role: userData.role?._id || userData.role,
            obras: userData.obras,
            createdBy: loggedInUserData?._id,
            enabled: true,
            mailNotification: !!userData.mailNotification
        }

        console.log('Payload to be sent:', payload)

        const result = await createUser(payload, userData.password)

        if (result && result.success) {
            setOpenLoader(false)
            setStatus({
                status: 'completed',
                title: 'Usuario Enrolado',
                message: `El usuario ${userData.name} ${userData.lastName} ha sido enrolado exitosamente.`,
                severity: 'success'
            })
            openCloseModal()
        } else {
            setOpenLoader(false)
            const errorMsg = typeof result?.error === 'string'
                ? result.error
                : (Array.isArray(result?.error) ? result.error.join(', ') : 'Error al crear el usuario.')
            const code = result?.statusCode || '400'

            setStatus({
                status: 'error',
                title: 'Error al Enrolar Usuario',
                message: 'No se pudo registrar el nuevo usuario.',
                errorDetails: {
                    code: code,
                    info: errorMsg
                },
                severity: 'error'
            })
        }
    }

    const editUser = async () => {
        setOpenLoader(true)
        const payload = { ...userData }

        if (payload.role && typeof payload.role === 'object') {
            payload.role = payload.role._id
        }

        if (
            payload.obras &&
            Array.isArray(payload.obras) &&
            payload.obras.length > 0 &&
            typeof payload.obras[0] === 'object' &&
            payload.obras[0] !== null
        ) {
            payload.obras = payload.obras.map((obra) => obra._id)
        }

        try {
            const response = await usersRoutes.editUser(payload, id)
            if (response.status === 200 || response.status === 201 || response.data?.status === 'ok' || response.data?.data) {
                setStatus({
                    status: 'completed',
                    title: 'Usuario Actualizado',
                    message: `Los datos del usuario ${userData.name} ${userData.lastName} fueron actualizados correctamente.`,
                    severity: 'success'
                })
                openCloseModal()
                await getUsers()
            }
        } catch (err) {
            console.error('Error updating user:', err)
            const serverMessage = err.response?.data?.message
            const errorMsg = Array.isArray(serverMessage)
                ? serverMessage.join(', ')
                : (typeof serverMessage === 'string' ? serverMessage : (err.message || 'Error al editar usuario. Verifique los datos ingresados.'))
            const code = err.response?.status || err.response?.data?.statusCode || '400'

            setStatus({
                status: 'error',
                title: 'Error al Editar Usuario',
                message: 'No se pudieron actualizar los datos del usuario.',
                errorDetails: {
                    code: code,
                    info: errorMsg
                },
                severity: 'error'
            })
        } finally {
            setOpenLoader(false)
        }
    }

    const deleteData = () => {
        setUserData(userDataDefault)
    }

    const getUserInfo = async () => {
        if (!userData.rut) {
            setStatus({
                status: 'error',
                title: 'Campo Requerido',
                message: 'Por favor ingrese un RUT válido para el usuario.',
                severity: 'warning'
            })
            return
        }
        if (!id && userData.rut && users) {
            const cleanInputRut = userData.rut.replace(/\./g, '')
            const existing = users.find(u => u.rut && u.rut.replace(/\./g, '') === cleanInputRut)
            if (existing) {
                setStatus({
                    status: 'error',
                    title: 'RUT Ya Registrado',
                    message: `El RUT ingresado ya está registrado a nombre de ${existing.name} ${existing.lastName}. Por favor verifique los datos o edite al usuario existente.`,
                    severity: 'warning'
                })
                return
            }
        }
        if (!userData.name) {
            setStatus({
                status: 'error',
                title: 'Campo Requerido',
                message: 'Por favor ingrese el nombre del usuario.',
                severity: 'warning'
            })
            return
        }
        if (!userData.lastName) {
            setStatus({
                status: 'error',
                title: 'Campo Requerido',
                message: 'Por favor ingrese el apellido del usuario.',
                severity: 'warning'
            })
            return
        }
        if (!userData.obras || userData.obras.length === 0) {
            setStatus({
                status: 'error',
                title: 'Campo Requerido',
                message: 'Debe seleccionar al menos una obra (máximo 3).',
                severity: 'warning'
            })
            return
        }
        if (userData.obras.length > 3) {
            setStatus({
                status: 'error',
                title: 'Límite Superado',
                message: 'Solo se permite asociar un máximo de 3 obras por usuario.',
                severity: 'warning'
            })
            return
        }
        if (!userData.role) {
            setStatus({
                status: 'error',
                title: 'Campo Requerido',
                message: 'Seleccione un rol de usuario para continuar.',
                severity: 'warning'
            })
            return
        }

        if (id) {
            setUsageModule(usageModule + 1)
        } else {
            if (!userData.password || userData.password.length < 6) {
                setStatus({
                    status: 'error',
                    title: 'Contraseña Inválida',
                    message: 'La contraseña debe contar con al menos 6 caracteres.',
                    severity: 'warning'
                })
                return
            }
            if (!userData.confirmPassword || userData.confirmPassword.length < 6) {
                setStatus({
                    status: 'error',
                    title: 'Confirmación Inválida',
                    message: 'La confirmación de contraseña debe contar con al menos 6 caracteres.',
                    severity: 'warning'
                })
                return
            }
            if (userData.confirmPassword !== userData.password) {
                setStatus({
                    status: 'error',
                    title: 'Contraseñas Mismatched',
                    message: 'La contraseña y su confirmación no coinciden.',
                    severity: 'warning'
                })
                return
            }
            setUsageModule(usageModule + 1)
        }
    }

    return (
        <Box height='100%'>
            <Grid className={'root'} container spacing={0}>
                <Grid className={'pageContainer'} item xs={12}>
                    <Card elevation={0} className={'pageCard'}>
                        <Grid container alignItems='center' justifyContent='center'>
                            <div style={{ width: '100%', textAlign: 'left', padding: 10 }}>
                                <div style={{ width: '100%', textAlign: 'left', color: '#333', backgroundColor: '#fff', borderRadius: 20 }}>
                                    <Toolbar style={{ paddingLeft: 0, backgroundColor: '#F9F9F9', borderRadius: 10 }}>
                                        <IconButton onClick={() => setTimeout(() => {
                                            navigate(-1)
                                            deleteData()
                                        }, 500)}>
                                            <ArrowBackIos style={{ color: '#333', fontSize: 16 }} />
                                        </IconButton>
                                        <h1 style={{ marginTop: 0, marginBottom: 0, fontSize: 16 }}>
                                            Administración / Administrar usuarios / {routingData}
                                        </h1>
                                    </Toolbar>
                                </div>
                            </div>
                        </Grid>
                        <Grid container alignItems='center' justifyContent='center'>
                            <div style={{ width: '100%', textAlign: 'left', padding: '10px 15px 80px 15px', overflowY: 'auto', maxHeight: 'calc(100vh - 120px)' }}>
                                {
                                    (usageModule === 0) &&
                                    <CreateUser typeDisplay={routingData} uData={userData} setUserData={setUserData} />
                                }
                                {(usageModule === 1) &&
                                    <PermissionUser width={'calc(100vw - 350px)'} height={'calc(100vh - 320px)'} typeDisplay={routingData} id={id} userData={userData} />
                                }
                            </div>
                            <Box
                                sx={{
                                    position: 'fixed',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: 65,
                                    backgroundColor: '#ffffff',
                                    borderTop: '1px solid #e0e0e0',
                                    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.08)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    px: 3,
                                    zIndex: 1000
                                }}
                            >
                                <div style={{ display: 'flex', gap: 15, alignItems: 'center' }}>
                                    {
                                        usageModule === 1 &&
                                        <button onClick={() => { setUsageModule(usageModule - 1) }} style={{ width: 170, height: 44, borderRadius: 22, fontSize: 16, fontWeight: 'bold', color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D', cursor: 'pointer' }}>
                                            Modificar Datos
                                        </button>
                                    }
                                    <button onClick={() => { navigate(-1); deleteData() }} style={{ width: 170, height: 44, borderRadius: 22, fontSize: 16, fontWeight: 'bold', color: '#BB2D2D', borderColor: '#BB2D2D', borderWidth: 2, backgroundColor: '#fff', cursor: 'pointer' }}>
                                        Cancelar
                                    </button>
                                    {
                                        usageModule === 0 &&
                                        <button onClick={() => getUserInfo()} style={{ width: 170, height: 44, borderRadius: 22, fontSize: 16, fontWeight: 'bold', color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D', cursor: 'pointer' }}>
                                            Siguiente
                                        </button>
                                    }
                                    {
                                        ((usageModule > 0) && !id) &&
                                        <button onClick={() => { saveUser() }} style={{ width: 170, height: 44, borderRadius: 22, fontSize: 16, fontWeight: 'bold', color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D', cursor: 'pointer' }}>
                                            Crear usuario
                                        </button>
                                    }
                                    {
                                        ((usageModule > 0) && id) &&
                                        <button onClick={() => { editUser() }} style={{ width: 170, height: 44, borderRadius: 22, fontSize: 16, fontWeight: 'bold', color: '#fff', backgroundColor: '#BB2D2D', borderColor: '#BB2D2D', cursor: 'pointer' }}>
                                            Editar usuario
                                        </button>
                                    }
                                </div>
                            </Box>
                        </Grid>
                        <div>
                            <Modal
                                open={open}
                                aria-labelledby="modal-modal-title"
                                aria-describedby="modal-modal-description"
                            >
                                <Box sx={styleModal}>
                                    <div style={{ textAlign: 'center' }}>
                                        <img src="../../assets/icons/check-green.svg" alt="" />
                                        {(routingData === 'Nuevo usuario') &&
                                            <h1>Usuario creado con éxito</h1>}
                                        {(routingData === 'Editar usuario') &&
                                            <h1>Usuario editado con éxito</h1>}
                                    </div>
                                    <Fab onClick={openCloseModal} style={{ position: 'absolute', right: 10, top: 10, boxShadow: 'none', backgroundColor: 'transparent' }}>
                                        <Close style={{ color: '#ccc' }} />
                                    </Fab>
                                </Box>
                            </Modal>
                            <LoadingLogoDialog open={openLoader} />
                            <GenerationStatusModal />
                        </div>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    )
}

export default AdminNewUserPage
